import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { clearActingAthleteStorage } from './ActingAthleteContext';
import { getAttribution, clearAttribution } from '../lib/attribution';
import { clientProofHeaders, proofDue } from '../lib/clientProof';

const AuthContext = createContext(null);

// The server refused the session itself (401/403). Anything else (5xx, 429,
// no response) is an outage on our side and must not sign the user out.
const sessionRefused = (err) => {
  const status = err?.response?.status;
  return status === 401 || status === 403;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const getApiUrl = () => {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port;
    
    if (port && port !== '80' && port !== '443') {
      return `${protocol}//${hostname}:${port}`;
    }
    return `${protocol}//${hostname}`;
  };
  
  const API_URL = getApiUrl() + '/api';

  const login = async (email, password) => {
    try {
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);

      const response = await axios.post(`${API_URL}/auth/login`, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          ...(await clientProofHeaders(API_URL, 'login')),
        }
      });

      const { access_token, refresh_token, user: userData } = response.data;

      localStorage.setItem('token', access_token);
      if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return userData;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Reminder-email sign-in link: exchange the one-time token for a session,
  // stored exactly as a password login stores it.
  const loginWithActivationToken = async (activationToken) => {
    const response = await axios.post(`${API_URL}/auth/activation-signin`, { token: activationToken });
    const { access_token, refresh_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
    setToken(access_token);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    return userData;
  };

  // NEW: Google OAuth login
  const loginWithGoogle = async (credential) => {
    try {
      console.log('Attempting Google login to:', `${API_URL}/auth/google/login`);
      
      const response = await axios.post(`${API_URL}/auth/google/login`, {
        credential: credential,
        // First-touch attribution, forwarded exactly as the register pages do.
        // "Sign up with Google" is plausibly the majority signup route, so an
        // OAuth path that sends nothing means every paid conversion through it
        // reads as organic. The server ignores this for an existing user.
        acquisition: getAttribution() || undefined,
      }, { headers: await clientProofHeaders(API_URL, 'login') });

      console.log('Google login successful!', response.data);

      const { access_token, refresh_token, user: userData } = response.data;

      // Whether this was a signup or a returning sign-in, the record has now
      // been offered to the server; clearing it stops a shared browser from
      // attributing later accounts to the first visitor's ad click.
      clearAttribution();

      localStorage.setItem('token', access_token);
      if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return userData;
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  };

  const register = async (email, password, firstName, lastName, { termsAccepted = false } = {}) => {
    // Legacy 5-field signature kept for any consumer that still calls it.
    return registerFull({
      email,
      password,
      first_name: firstName,
      last_name: lastName,
      terms_accepted: termsAccepted,
    });
  };

  /**
   * Full-fidelity register call matching the iOS RegisterRequest payload.
   * Posts every field the backend's UserCreate schema accepts (school_*,
   * phone, dob, guardian1_*, registration_type + athlete_* for the parent
   * flow, etc.), then auto-logs the user in so the next render lands them
   * on /dashboard rather than back at the login page.
   *
   * Returns { user, unverified }. When `unverified` is true the caller
   * should show a "check your email" screen instead of navigating to the
   * dashboard.
   */
  const registerFull = async (payload) => {
    try {
      await axios.post(`${API_URL}/auth/register`, payload,
        { headers: await clientProofHeaders(API_URL, 'register') });
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }

    // Backend doesn't return a token from /auth/register — it always
    // requires a follow-up /auth/login. Auto-login here so the caller
    // doesn't have to chain it.
    try {
      const userData = await login(payload.email, payload.password);
      const unverified = userData && userData.is_email_verified === false;
      return { user: userData, unverified };
    } catch (loginError) {
      // Registration succeeded but auto-login failed (rare — usually
      // means email verification is enforced and the password gate
      // rejected the unverified account). Surface a soft signal so the
      // UI can show the verify-pending screen instead of erroring out.
      console.warn('Auto-login after register failed:', loginError);
      return { user: null, unverified: true };
    }
  };

  const resendVerificationEmail = async (email) => {
    return axios.post(`${API_URL}/auth/resend-verification-public`, { email });
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Error refreshing user:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    // Wipe acting-athlete state so the next sign-in (any user) starts
    // fresh — without this, a guardian's stored as_athlete_id would
    // bleed into the next session and silently scope every API call
    // to an athlete the new user may not be authorized for.
    clearActingAthleteStorage();
  };

  // Auto-refresh on 401, then logout only if the server refuses the refresh
  useEffect(() => {
    let isRefreshing = false;
    let pendingQueue = [];

    const processQueue = (error, newToken = null) => {
      pendingQueue.forEach(({ resolve, reject }) => {
        if (error) reject(error);
        else resolve(newToken);
      });
      pendingQueue = [];
    };

    const interceptor = axios.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;
        const url = originalRequest?.url || '';
        const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/google') || url.includes('/auth/apple') || url.includes('/auth/register') || url.includes('/auth/refresh');

        if (error.response?.status === 401 && token && !isAuthEndpoint && !originalRequest._retry) {
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              pendingQueue.push({ resolve, reject });
            }).then(newToken => {
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return axios(originalRequest);
            });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          const refreshToken = localStorage.getItem('refresh_token');
          try {
            const refreshBody = refreshToken ? { refresh_token: refreshToken } : {};
            const baseHeaders = refreshToken ? {} : { Authorization: `Bearer ${token}` };
            const firstProof = proofDue() ? await clientProofHeaders(API_URL, 'refresh') : {};
            let res;
            try {
              res = await axios.post(`${API_URL}/auth/refresh`, refreshBody,
                { headers: { ...baseHeaders, ...firstProof } });
            } catch (refreshErr) {
              // 428 = the server wants a proof we did not send. Retry once with one.
              if (refreshErr.response?.status !== 428 || firstProof['X-Client-Proof']) throw refreshErr;
              const retryProof = await clientProofHeaders(API_URL, 'refresh');
              if (!retryProof['X-Client-Proof']) {
                // Our challenge failed, so no proof could be built. That is
                // not the user's fault: fail this request, keep the session.
                refreshErr.clientProofUnavailable = true;
                throw refreshErr;
              }
              res = await axios.post(`${API_URL}/auth/refresh`, refreshBody,
                { headers: { ...baseHeaders, ...retryProof } });
            }
            const { access_token, refresh_token: newRefresh } = res.data;
            localStorage.setItem('token', access_token);
            if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
            setToken(access_token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
            processQueue(null, access_token);
            originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
            return axios(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError, null);
            // Only a refusal ends the session. An outage, a network error or
            // a rate limit (429) is ours, not the user's: fail this request,
            // keep the session.
            if (!refreshError.clientProofUnavailable && sessionRefused(refreshError)) logout();
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token]);

  // Startup session check. Declared AFTER the 401 interceptor effect on
  // purpose: effects run in declaration order and axios builds its
  // interceptor chain when a request is made, so a /users/me sent before
  // the interceptor exists never gets the chance to refresh.
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get(`${API_URL}/users/me`)
        .then(response => {
          setUser(response.data);
          setLoading(false);
        })
        .catch(err => {
          if (err?.clientProofUnavailable || !sessionRefused(err)) {
            // Our side failed, not the user's session: no proof could be built
            // (our challenge failed), the server erred or rate-limited us, or
            // the network did. Drop the in-memory session so the app shows sign-in, but keep the
            // stored tokens so a reload retries rather than forcing a fresh
            // sign-in. A refused refresh has already logged out in the
            // interceptor.
            setToken(null);
            delete axios.defaults.headers.common['Authorization'];
          } else {
            logout();
          }
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const value = {
    user,
    token,
    loading,
    login,
    loginWithGoogle,
    loginWithActivationToken,
    register,
    registerFull,
    resendVerificationEmail,
    logout,
    refreshUser,
    isAuthenticated: !!token,
    // A partner's team member arrives with wire role 'partner'; only the partner manages the team.
    isPartnerMember: !!user?.is_partner_member,
    API_URL
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
